import { forwardRef } from "react";

export type Props = {
  /** Field name for identification */
  name: string;
  /** Image to display (for quiz questions) */
  image?: string;
  /** Label text */
  label?: string;
  /** Current value */
  value?: boolean;
  /** Change handler */
  onChange?: (value: boolean) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
};

/**
 * A hidden input that always returns true.
 * Used in quiz forms where a question requires forced acknowledgment.
 *
 * This is a pure UI component - works with or without react-hook-form.
 *
 * @example
 * // Standalone usage
 * <ForceYesInput
 *   name="acknowledge"
 *   value={true}
 *   onChange={(val) => console.log(val)}
 * />
 *
 * @example
 * // With ControlledForceYesInput (RHF)
 * <ControlledForceYesInput name="acknowledge" />
 */
export const ForceYesInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      // image and label are accepted for API compatibility with QuizFormField
      // but not rendered since this is a hidden input
      image: _image,
      label: _label,
      value = true,
      onChange,
      onBlur,
      error,
      disabled,
      className,
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.checked);
    };

    return (
      <div className={className}>
        {/* Hidden input that always returns boolean true */}
        <input
          ref={ref}
          type="checkbox"
          name={name}
          checked={value}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          aria-invalid={error || undefined}
          data-testid={`force-yes-input-${name}`}
          hidden
        />
      </div>
    );
  }
);

ForceYesInput.displayName = "ForceYesInput";

export default ForceYesInput;

